import { getTitleStrings } from './utils'
import { fetchIndustries } from './api'

class AgentPrompts {
  constructor (discoveryState) {
    this.discoveryState = discoveryState
    this.previousState = null
  }

  static monitorAgentPrompt () {
    return `
      You are an assistant that classifies conversation topics into one of the predefined categories. Based on the conversation, identify the topic and respond in the following JSON format: 
      {
        "current_topic": "<one of: CURRENT_JOB_TITLE_ROLE, PAST_JOB_TITLE_ROLE, LOCATION, INDUSTRY, COMPANY_NAME, COMPANY_STAGE, COMPANY_BUSINESS_MODEL, COMPANY_SIZE, SKILLS_MUSTHAVE, SKILLS_NICETOHAVE, OVERALL_EXPERIENCE, SCHOOL_NAME, DEGREE, MAJOR, OTHER>"
      }
      If the topic does not fit any of the predefined categories, classify it as OTHER.
      Example User Input: "I am currently working as a software engineer at Google."
      Expected Output: {
        "current_topic": "CURRENT_JOB_TITLE_ROLE"
      }`
  }
  
  static detectUserConfirmation() {
    return `
      You are an assistant that analyzes user messages to determine if they contain a confirmation to proceed to the next topic.
      
      Look for phrases that indicate the user is confirming, agreeing, or giving permission to move forward, such as:
      - "Yes"
      - "Sure"
      - "That's correct"
      - "Looks good"
      - "Proceed"
      - "Let's move on"
      - "That works"
      - "I agree"
      - "That's right"
      - "Correct"
      
      Respond with the following JSON format:
      {
        "user_confirmed": true/false,
        "confidence": <number between 0 and 1>,
        "detected_phrase": "<the phrase that indicates confirmation, if any>"
      }
      
      Example User Input: "Yes, that looks good to me."
      Expected Output: {
        "user_confirmed": true,
        "confidence": 0.95,
        "detected_phrase": "Yes, that looks good to me"
      }
      
      Example User Input: "No, that's not what I meant."
      Expected Output: {
        "user_confirmed": false,
        "confidence": 0.9,
        "detected_phrase": "No, that's not what I meant"
      }
      
      Example User Input: "I'm not sure about that."
      Expected Output: {
        "user_confirmed": false,
        "confidence": 0.7,
        "detected_phrase": "I'm not sure about that"
      }
      
      Example User Input: "Tell me more about the job requirements."
      Expected Output: {
        "user_confirmed": false,
        "confidence": 0.8,
        "detected_phrase": null
      }
    `
  }

  static monitorAgentPromptWithDiscovery (discoveryState) {
    // Map discovery topics to the categories used in the original monitorAgentPrompt
    const topicMapping = {
      'skills': ['SKILLS_MUSTHAVE', 'SKILLS_NICETOHAVE'],
      'location': ['LOCATION'],
      'title': ['CURRENT_JOB_TITLE_ROLE', 'PAST_JOB_TITLE_ROLE'],
      'company': ['COMPANY_NAME', 'COMPANY_STAGE', 'COMPANY_BUSINESS_MODEL', 'COMPANY_SIZE', 'INDUSTRY'],
      'experience': ['OVERALL_EXPERIENCE'],
      'education': ['SCHOOL_NAME', 'DEGREE', 'MAJOR']
    };
    
    // Log the topic mapping for debugging
    console.log('Topic mapping for discovery:', topicMapping);
    
    // Get the current discovery topic if available
    let currentDiscoTopic = null;
    let discoveryOrder = [];
    let completedTopics = [];
    
    if (discoveryState && discoveryState.currentDiscoTopic) {
      currentDiscoTopic = discoveryState.currentDiscoTopic;
      discoveryOrder = discoveryState.discoveryOrder || [];
      completedTopics = discoveryState.completedTopics || [];
    }
    
    // Create a string with the current discovery state for context
    let discoveryContext = '';
    if (currentDiscoTopic) {
      discoveryContext = `
        Current discovery context:
        - Current topic: ${currentDiscoTopic}
        - Discovery order: ${discoveryOrder.join(', ')}
        - Completed topics: ${completedTopics.join(', ')}
      `;
    }
    
    return `
      You are an assistant that classifies conversation topics into one of the predefined categories. Based on the conversation, identify the topic and respond in the following JSON format: 
      {
        "current_topic": "<one of: CURRENT_JOB_TITLE_ROLE, PAST_JOB_TITLE_ROLE, LOCATION, INDUSTRY, COMPANY_NAME, COMPANY_STAGE, COMPANY_BUSINESS_MODEL, COMPANY_SIZE, SKILLS_MUSTHAVE, SKILLS_NICETOHAVE, OVERALL_EXPERIENCE, SCHOOL_NAME, DEGREE, MAJOR, OTHER>",
        "discovery_topic": "<one of: skills, location, title, company, experience, education, or null if not related to discovery>"
      }
      
      ${discoveryContext}
      
      If the topic does not fit any of the predefined categories, classify it as OTHER.
      
      Map the current_topic to the appropriate discovery_topic using these mappings:
      - SKILLS_MUSTHAVE, SKILLS_NICETOHAVE → skills
      - LOCATION → location
      - CURRENT_JOB_TITLE_ROLE, PAST_JOB_TITLE_ROLE → title
      - COMPANY_NAME, COMPANY_STAGE, COMPANY_BUSINESS_MODEL, COMPANY_SIZE, INDUSTRY → company
      - OVERALL_EXPERIENCE → experience
      - SCHOOL_NAME, DEGREE, MAJOR → education
      
      Example User Input: "I am currently working as a software engineer at Google."
      Expected Output: {
        "current_topic": "CURRENT_JOB_TITLE_ROLE",
        "discovery_topic": "title"
      }
      
      Example User Input: "I have experience with Python, JavaScript, and React."
      Expected Output: {
        "current_topic": "SKILLS_MUSTHAVE",
        "discovery_topic": "skills"
      }
      
      Example User Input: "I'm looking for a job in San Francisco or New York."
      Expected Output: {
        "current_topic": "LOCATION",
        "discovery_topic": "location"
      }`
  }

  static monitorAndExtractTitlesAndSkillsPrompt() {
    return `
      You are an intelligent assistant designed to interpret user input and identify potential job titles and/or skills. Be cautious, as the user may misstate or use informal language. Your goal is to carefully analyze the input, infer meaning where appropriate, and ensure accuracy in recommending standardized titles and skills.
  
      Here’s what you need to do:
      - **Carefully review** the user’s message to identify references to job titles and skills.
      - If the input contains informal or unconventional terms (e.g., "ambulance chaser"), interpret their meaning in context (e.g., "lawyer" or "legal professional").
      - If the input describes roles, responsibilities, or achievements (e.g., "P&L owner"), infer potential titles (e.g., "General Manager," "Director") and relevant skills.
      - Acknowledge that users may misspeak or be unclear. Use caution to avoid over-interpreting vague or ambiguous inputs.
      - Provide recommendations only if there is enough context to make reasonable assumptions.
  
      Format your response as:
      {
        "titles": ["<deduced title 1>", "<deduced title 2>", "..."],
        "skills": ["<deduced skill 1>", "<deduced skill 2>", "..."]
      }
  
      If the user’s message is unclear or lacks enough information, respond conservatively:
      {
        "titles": [],
        "skills": []
      }
  
      ### Examples
  
      **User Input**: "I used to work as an ambulance chaser and handled many personal injury cases."
      **Expected Output**:
      {
        "titles": ["lawyer", "personal injury attorney"],
        "skills": ["litigation", "personal injury law"]
      }
  
      **User Input**: "I manage P&L for my team and lead business growth initiatives."
      **Expected Output**:
      {
        "titles": ["General Manager", "Business Director"],
        "skills": ["P&L management", "business strategy", "team leadership"]
      }
  
      **User Input**: "I’m skilled in building scalable web apps and have experience in React and Node.js."
      **Expected Output**:
      {
        "titles": [],
        "skills": ["web application development", "React", "Node.js"]
      }
  
      **User Input**: "I worked on the company's go-to-market strategy and customer acquisition."
      **Expected Output**:
      {
        "titles": ["Marketing Manager", "Go-to-Market Strategist"],
        "skills": ["go-to-market strategy", "customer acquisition"]
      }
  
      **User Input**: "I am amazing at everything tech."
      **Expected Output**:
      {
        "titles": [],
        "skills": []
      }
  
      ### Instructions:
      - **Be cautious** when analyzing ambiguous or unclear statements.
      - Avoid making assumptions without sufficient context. Recommend only what is well-supported by the input.
      - Consider the broader context and industry norms to provide reasonable and meaningful recommendations.
      - If uncertain, lean toward a conservative response to avoid over-interpreting.
  
      If the input does not reference titles or skills, or if it is too vague to interpret, return:
      {
        "titles": [],
        "skills": []
      }
    `;
  }

  static monitorAndExtractAgentPrompt (topic) {
    return `
      You are an assistant that monitors conversations for a specific topic: "${topic}". Use this topic as the context of the conversation to extract relevant values shared by the user.
  
      If the user gives a non standard role/title - convert that into 2-3 options of a standardized role / title.
      If the user is discussing anything related to the topic "${topic}", respond with the following JSON format:
      {
        "current_topic": "${topic}",
        "values": ["<extracted value 1>", "<extracted value 2>", "..."]
      }
      
      If no values can be extracted, set "values" to an empty array.
  
      Examples:
      Example Topic: "PAST_JOB_TITLE_ROLES"
      User Input: "I used to work as a software engineer and a team lead at Amazon."
      Expected Output:
      {
        "current_topic": "PAST_JOB_TITLE_ROLES",
        "values": ["software engineer", "team lead"]
      }
  
      Example Topic: "LOCATION"
      User Input: "I live in New York City, but I've also spent time working in Boston and Chicago."
      Expected Output:
      {
        "current_topic": "LOCATION",
        "values": ["New York City", "Boston", "Chicago"]
      }
  
      Your task is to focus on extracting concise and relevant values specifically related to the topic: "${topic}". If the conversation does not align with this topic or no values can be extracted, return:
      {
        "current_topic": "${topic}",
        "values": []
      }
    `
  }

  updateToApprovalPendingState () {
    this.previousState = { ...this.discoveryState } // Clone to prevent reference issues
    this.discoveryState.status = 'ApprovalPending'
  }

  updateRejectPendingState () {
    if (this.previousState) {
      this.discoveryState = { ...this.previousState } // Revert to the previous state
      this.previousState = null // Clear the previous state
    } else {
      console.warn('No previous state to revert to.') // Optional warning
    }
  }

  updateConfirmApproval () {
    if (this.discoveryState.status !== 'ApprovalPending') {
      console.warn('State is not ApprovalPending. Cannot confirm approval.')
      return
    }

    switch (this.previousState?.status) {
      case 'TitleDiscovery':
        this.discoveryState.status = 'IndustryDiscovery'
        break
      case 'IndustryDiscovery':
        this.discoveryState.status = 'CompanyDiscovery'
        break
      case 'CompanyDiscovery':
        this.discoveryState.status = 'LocationDiscovery'
        break
      case 'LocationDiscovery':
        this.discoveryState.status = 'ExperienceDiscovery'
        break
      case 'ExperienceDiscovery':
        this.discoveryState.status = 'EducationDiscovery'
        break
      case 'EducationDiscovery':
        this.discoveryState.status = 'SkillsDiscovery'
        break
      case 'SkillsDiscovery':
        this.discoveryState.status = 'DiscoveryDone'
        break
      default:
        console.error('Unknown discoveryState:', this.previousState?.status)
        throw new Error('Unknown discoveryState')
    }
    this.previousState = null
  }

  static generatePrompt (topic, values) {
    if (topic == 'CURRENT_JOB_TITLE_ROLE') {
      if (values) {
        return `
          You are assisting with a title discovery task. Your goal is to verify if the proposed titles (${values.toString()}) are representative of the role being pursued. Engage with the user in a natural, friendly, and collaborative manner, making it clear that these are directional titles.
          Ask the user to pick one of the following options from the below buttons:
            •	Looks Good
            •	Doesn’t Look Good
          Based on their choice, refine your approach as needed.
        `
      } else {
        return `
          You are assisting with a title discovery task. Engage with the user in a friendly and collaborative tone, encouraging them to share their thoughts.

          Ask open-ended questions to refine your suggestions, such as:
            •	“Do these titles align with what you’re looking for?”
            •	“Are there any adjustments or alternatives you’d suggest?”

          Your goal is to guide the user and collaboratively arrive at the most accurate titles.        
        `
      }
    }

    if (topic == 'PAST_JOB_TITLE_ROLE') {
      if (values) {
        return `
          You are assisting with a past title discovery task. Your goal is to verify if the proposed past titles (${values.toString()}) are representative of the role being pursued. Engage with the user in a natural, friendly, and collaborative manner, making it clear that these are directional titles.
          Ask the user to pick one of the following options from the below:
            •	Looks Good
            •	Doesn’t Look Good
          Based on their choice, refine your approach as needed.
        `
      } else {
        return `
          You are assisting with a past title discovery task. Engage with the user in a friendly and collaborative tone, encouraging them to share their thoughts.

          Ask open-ended questions to refine your suggestions, such as:
            •	“Do these titles align with what you’re looking for?”
            •	“Are there any adjustments or alternatives you’d suggest?”

          Your goal is to guide the user and collaboratively arrive at the most accurate titles.        
        `
      }
    }
  }

  async getPromptForState (userMessage, matches, queries) {
    switch (this.discoveryState?.status) {
      case 'ApprovalPending':
        return "Awaiting user's feedback on the proposed suggestions."
      case 'TitleDiscovery':
        let roles = getTitleStrings(matches, queries)

        return `
            You are assisting in a title discovery task. Your goal is to engage in a conversation with the user to collaboratively ask the users if the proposed titles are accurately captured.
            Make sure your conversation with the user is natural, friendly, and collaborative. Ask the user if we captured these roles ${roles.toString()} correctly.`
      case 'IndustryDiscovery':
        let industries = await fetchIndustries()
        return `
            You are now assisting user in discovering target industries. Your goal is to engage in a conversation with the user to collaboratively discover from the user the ideal industries (if any). Use the user input and map the user feedback into one or more of this ${industries.join(
              ','
            )}. 
            Make sure your conversation with the user is natural, friendly, and collaborative and only focus on gatehring industry. Use this chat context "${userMessage}" and once the user selection is done - signal back to us `
      case 'CompanyDiscovery':
        return `
            You are now assisting user in a company discovery. Your goal is to engage in a conversation with the user to collaboratively discover from the user the ideal companies or company indicators. Company indicators are defined as specific stage (public, early starup or late startup), industries, company sizes, names, standard company lists etc.
            Make sure your conversation with the user is natural, friendly, and collaborative. Use this chat context "${userMessage}"`
      case 'LocationDiscovery':
        return `Agent prompt: Based on the user's input "${userMessage}", clarify the preferred work location.`
      case 'ExperienceDiscovery':
        return `Agent prompt: Based on the user's input "${userMessage}", extract years of experience and key skills.`
      case 'EducationDiscovery':
        return `Agent prompt: Based on the user's input "${userMessage}", infer the required education level.`
      case 'SkillsDiscovery':
        return `Agent prompt: Based on the user's input "${userMessage}", identify 3 key technical or soft skills.`
      default:
        return `Agent prompt: Process the user's input "${userMessage}" for any actionable discovery insights.`
    }
  }

  static processAgentMessage (agentMessage) {
    if (agentMessage.startsWith('[AGENT-INSTRUCTION]')) {
      const payload = agentMessage.replace('[AGENT-INSTRUCTION]', '').trim()
      try {
        return JSON.parse(payload)
      } catch (err) {
        console.error('Failed to parse agent message:', err, payload)
        return null
      }
    }
    return null
  }
}

export default AgentPrompts
